import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody } from "reactstrap";

import removeAssignee from "../../assets/img/icons/remove_assignee_black.svg";
import { ConfirmButton } from "../../styledComponents/buttons";
import {
  CardTitle,
  CommonFlex,
  CommonGrid,
  CommonImage,
  FormAlert,
  InputField,
} from "../../styledComponents/common";
import { checkHtml, checkUrl } from "../../utils/helper";
import { RemoveButton } from "../AddMultipleMemberDropDownNew/AddMultipleMemberDropDownNew";

/**
 * Reusable "Add/Edit Designation" modal.
 *
 * Props:
 * - isOpen: boolean
 * - toggle: () => void
 * - onSave: ({ name: string, description: string | null }) => void
 * - isSaving?: boolean
 * - initialValues?: { name?: string, description?: string }
 * - mode?: "add" | "edit" (optional; inferred from initialValues by default)
 */
const AddDesignationModal = ({
  isOpen,
  toggle,
  onSave,
  isSaving = false,
  initialValues = null,
  mode,
}) => {
  const { t } = useTranslation();
  const nameInputRef = useRef(null);

  const [designationName, setDesignationName] = useState("");
  const [saveErrors, setSaveErrors] = useState({});

  const computedMode = mode
    ? mode
    : initialValues && (initialValues.name || initialValues.description)
    ? "edit"
    : "add";

  const clearErrorsFor = key => {
    if (!saveErrors || !saveErrors[key]) return;
    const next = { ...saveErrors };
    delete next[key];
    setSaveErrors(next);
  };

  const handleToggle = () => {
    toggle();
  };

  const onNameChange = e => {
    const { value } = e.target;
    setDesignationName(value);
    clearErrorsFor("designationName");
  };

  const validate = () => {
    let errors = {};
    let formIsValid = true;

    const name = (designationName || "").trim();

    if (!name) {
      formIsValid = false;
      errors["designationName"] = t("designation_name_is_required");
    } else if (name.length > 256) {
      formIsValid = false;
      errors["designationName"] = t(
        "designation_name_must_be_within_256_chars",
      );
    } else if (checkUrl(name)) {
      formIsValid = false;
      errors["designationName"] = t("designation_name_can_not_contain_url");
    } else if (checkHtml(name)) {
      formIsValid = false;
      errors["designationName"] = t("invalid_designation_name");
    }

    setSaveErrors(errors);
    return formIsValid;
  };

  const handleSave = () => {
    if (isSaving) return;
    if (!validate()) return;

    const payload = {
      name: designationName.trim(),
      description: null,
    };

    if (onSave) onSave(payload);
  };

  useEffect(() => {
    if (!isOpen) return;

    setDesignationName(initialValues?.name || "");
    setSaveErrors({});

    const timer = setTimeout(() => {
      if (nameInputRef.current && nameInputRef.current.focus) {
        nameInputRef.current.focus();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen, initialValues]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ height: "auto", width: "348px" }}
    >
      <ModalBody
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "20px",
        }}
      >
        <CommonFlex justifyContent="space-between" alignItems="center">
          <CardTitle>
            {t(
              computedMode === "edit" ? "edit_designation" : "add_designation",
            )}
          </CardTitle>
          <RemoveButton onClick={handleToggle} title={t("close")}>
            <CommonImage src={removeAssignee} alt="close" size="8px" />
          </RemoveButton>
        </CommonFlex>

        <CommonGrid columns="1fr" gap="16px">
          <div>
            <InputField
              type="text"
              ref={nameInputRef}
              placeholder={t("write_designation_here")}
              value={designationName}
              onChange={onNameChange}
              error={
                saveErrors &&
                saveErrors["designationName"] &&
                saveErrors["designationName"] !== undefined
              }
            />
            {saveErrors &&
              saveErrors["designationName"] &&
              saveErrors["designationName"] !== undefined && (
                <FormAlert margin="0 0 0 5px">
                  {saveErrors["designationName"]}
                </FormAlert>
              )}
          </div>

          <ConfirmButton
            borderRadius="30px"
            width="100%"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving
              ? t("saving") || "Saving..."
              : t(
                  computedMode === "edit" ? "save_changes" : "save_designation",
                )}
          </ConfirmButton>
        </CommonGrid>
      </ModalBody>
    </Modal>
  );
};

export default AddDesignationModal;
